import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { AppDispatch, RootState } from "../../redux/store";
import { fetchUserData } from "../../redux/authSlice";
import logo from "../../assets/cosmoola/logo.jpg";
import {
  ArrowLeft, Plus, Eye, EyeOff, Copy, Check, Snowflake,
  Zap, Trash2, X, CheckCircle, AlertCircle, Loader2,
  Shield, Lock, CreditCard, Smartphone, ChevronRight,
  RotateCcw, Settings2, Wifi, Signal,
} from "lucide-react";

/* ─── THEME ─────────────────────────────────────────────── */
const D = {
  bg: "#000000", surface: "#080808", surface2: "#111111",
  border: "#1c1c1c", border2: "#2a2a2a", border3: "#333333",
  white: "#ffffff", offwhite: "#e8e8e8", muted: "#6b6b6b",
  subtle: "#3a3a3a", gold: "#C9A96E", goldLight: "#e8c98a",
  goldDark: "#a07840",
};
const L = {
  bg: "#ffffff", surface: "#fafafa", surface2: "#f3f3f3",
  border: "#e5e5e5", border2: "#d4d4d4", text: "#0a0a0a",
  textSoft: "#525252", muted: "#a3a3a3",
};

const API = "https://europe-west3-cosmoola.cloudfunctions.net";

const fmt = (n: number) =>
  "€" + Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 });
/* ─── BRAND CONFIG ─── */
type CardBrand = "visa" | "mastercard";
type CardType  = "virtual" | "physical";
type CardStatus = "active" | "inactive" | "canceled";

const BRAND_CFG = {
  visa: {
    label: "Visa", network: "VISA NETWORK",
    accent: "#C9A96E", liability: "Visa's Zero Liability Policy",
    description: "80M+ merchants globally",
    frontMark: () => React.createElement("span", { style: { fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:22, color:"#C9A96E", letterSpacing:"-0.02em", opacity:0.93 } }, "VISA"),
    backMark: (op: number) => React.createElement("span", { style: { fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:17, color:"#C9A96E", opacity:op } }, "VISA"),
  },
  mastercard: {
    label: "Mastercard", network: "MASTERCARD NETWORK",
    accent: "#f79e1b", liability: "Mastercard's Zero Liability Policy",
    description: "210+ countries · Priceless",
    frontMark: () => React.createElement("div", { style: { display:"flex", alignItems:"center" } },
      React.createElement("div", { style: { width:30, height:30, borderRadius:"50%", background:"#eb001b", opacity:0.93 } }),
      React.createElement("div", { style: { width:30, height:30, borderRadius:"50%", background:"#f79e1b", marginLeft:-15, opacity:0.89 } })
    ),
    backMark: (op: number) => React.createElement("div", { style: { display:"flex", opacity:op } },
      React.createElement("div", { style: { width:18, height:18, borderRadius:"50%", background:"#eb001b" } }),
      React.createElement("div", { style: { width:18, height:18, borderRadius:"50%", background:"#f79e1b", marginLeft:-9 } })
    ),
  },
} as const;

const getCfg = (brand: string) => BRAND_CFG[(brand?.toLowerCase() as CardBrand)] ?? BRAND_CFG.visa;



/* ─── ISSUED CARD TYPE ─── */
type IssuedCard = {
  id:               string;
  stripeCardId:     string;
  type:             "virtual" | "physical";
  brand:            string;   // actual brand issued by Stripe
  requestedBrand?:  string;   // what the user originally requested
  status:           "active" | "inactive" | "canceled";
  last4:            string;
  expMonth:         number;
  expYear:          number;
  currency:         string;
  spendingLimitEUR: number;
  spendingInterval: string;
  isDefault:        boolean;
  isIssued:         true;
  createdAt:        string | null;
};

/* ═══════════════════════════════════════════════════════════
   CARD VISUAL COMPONENT
   Full-size credit card with brand-specific design.
   Visa = deep black with gold   Mastercard = charcoal with red
════════════════════════════════════════════════════════════ */
const CardVisual: React.FC<{
  card: IssuedCard;
  holderName: string;
  revealed: boolean;
  pan?: string;     // full PAN when revealed
  cvv?: string;     // CVV when revealed
  flipped: boolean;
  onClick: () => void;
}> = ({ card, holderName, revealed, pan, cvv, flipped, onClick }) => {
  const isVisa = card.brand.toLowerCase() === "visa";
  const isActive = card.status === "active";
  const isFrozen = card.status === "inactive";
  const isCanceled = card.status === "canceled";

  /* Format PAN with spaces */
  const displayPan = revealed && pan
    ? pan.replace(/(\d{4})/g, "$1 ").trim()
    : `•••• •••• •••• ${card.last4}`;

  const expDisplay = `${String(card.expMonth).padStart(2, "0")} / ${String(card.expYear).slice(-2)}`;

  return (
    <div
      onClick={onClick}
      className="card-scene"
      style={{ width: "100%", maxWidth: 420, margin: "0 auto", perspective: 1000, cursor: "pointer" }}
    >
      <motion.div
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
        style={{ position: "relative", width: "100%", paddingBottom: "62%", transformStyle: "preserve-3d" }}
      >
        {/* ── FRONT ── */}
        <div style={{
          position: "absolute", inset: 0, backfaceVisibility: "hidden",
          borderRadius: 16,
          background: isVisa
            ? "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 40%, #0d0d0d 100%)"
            : "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f0f1a 100%)",
          boxShadow: isActive
            ? `0 24px 60px rgba(0,0,0,0.7), 0 0 0 1px ${D.border2}, inset 0 1px 0 rgba(255,255,255,0.06)`
            : `0 12px 30px rgba(0,0,0,0.5), 0 0 0 1px ${D.border}`,
          overflow: "hidden",
          opacity: isCanceled ? 0.4 : 1,
        }}>
          {/* Frosted overlay when frozen */}
          {isFrozen && (
            <div style={{
              position: "absolute", inset: 0, zIndex: 10,
              background: "rgba(0, 80, 160, 0.18)",
              backdropFilter: "blur(1px)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(0,0,0,0.6)", padding: "8px 18px", borderRadius: 100, border: `1px solid rgba(100,160,255,0.3)` }}>
                <Snowflake size={14} color="#90c0ff" />
                <span style={{ fontSize: 11, color: "#90c0ff", fontFamily: "'Montserrat',sans-serif", fontWeight: 600, letterSpacing: "0.2em" }}>FROZEN</span>
              </div>
            </div>
          )}

          {/* Geometric texture — diagonal lines */}
          <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.04 }} preserveAspectRatio="none">
            <defs>
              <pattern id={`lines-${card.id}`} x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse" patternTransform="rotate(35)">
                <line x1="0" y1="0" x2="0" y2="20" stroke="white" strokeWidth="0.8"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill={`url(#lines-${card.id})`}/>
          </svg>

          {/* Holographic shimmer oval */}
          <div style={{
            position: "absolute", top: "-30%", right: "-10%",
            width: "70%", height: "140%",
            background: isVisa
              ? "radial-gradient(ellipse, rgba(201,169,110,0.12) 0%, rgba(201,169,110,0.04) 50%, transparent 70%)"
              : "radial-gradient(ellipse, rgba(180,30,30,0.15) 0%, rgba(180,30,30,0.04) 50%, transparent 70%)",
            transform: "rotate(-20deg)",
          }} />

          {/* Second accent oval */}
          <div style={{
            position: "absolute", bottom: "-40%", left: "-15%",
            width: "60%", height: "120%",
            background: "radial-gradient(ellipse, rgba(255,255,255,0.03) 0%, transparent 60%)",
          }} />

          {/* Card content */}
          <div style={{ position: "relative", height: "100%", padding: "6% 8%", display: "flex", flexDirection: "column", justifyContent: "space-between", boxSizing: "border-box" }}>

            {/* Top row: logo + brand logo + status */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <img src={logo} alt="C" style={{ width: 28, height: 28, borderRadius: 4, objectFit: "cover", opacity: 0.95 }} />
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: D.gold, letterSpacing: "0.3em", fontFamily: "'Montserrat',sans-serif", lineHeight: 1 }}>COSMOOLA</div>
                  <div style={{ fontSize: 8, color: "rgba(255,255,255,0.35)", letterSpacing: "0.15em", fontFamily: "'Montserrat',sans-serif", marginTop: 1 }}>
                    {card.type === "virtual" ? "VIRTUAL" : "PREMIUM"}
                  </div>
                </div>
              </div>

              {/* Brand mark */}
              {isVisa ? (
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 700, fontSize: 22, color: D.gold, letterSpacing: "-0.02em", opacity: 0.9 }}>VISA</div>
              ) : (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#eb001b", opacity: 0.9 }} />
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#f79e1b", marginLeft: -14, opacity: 0.85 }} />
                </div>
              )}
            </div>

            {/* Chip + contactless */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: "2%" }}>
              {/* EMV chip */}
              <div style={{
                width: 38, height: 28, borderRadius: 4,
                background: "linear-gradient(135deg, #c8a84b, #e8c96a, #a07030, #d4a840)",
                boxShadow: "inset 0 1px 2px rgba(255,255,255,0.4), 0 1px 3px rgba(0,0,0,0.5)",
                position: "relative", overflow: "hidden",
              }}>
                <div style={{ position: "absolute", top: "33%", left: 0, right: 0, height: 1, background: "rgba(0,0,0,0.2)" }} />
                <div style={{ position: "absolute", left: "33%", top: 0, bottom: 0, width: 1, background: "rgba(0,0,0,0.15)" }} />
                <div style={{ position: "absolute", right: "33%", top: 0, bottom: 0, width: 1, background: "rgba(0,0,0,0.15)" }} />
              </div>
              {/* Contactless symbol */}
              <Wifi size={16} color="rgba(255,255,255,0.4)" style={{ transform: "rotate(90deg)" }} />
            </div>

            {/* PAN */}
            <div style={{
              fontFamily: "'Courier New', monospace", fontSize: "clamp(13px,3.5vw,18px)",
              color: revealed ? D.goldLight : "rgba(255,255,255,0.7)",
              letterSpacing: "0.2em", fontWeight: 500, marginTop: "1%",
              transition: "color 0.3s",
            }}>
              {displayPan}
            </div>

            {/* Bottom row */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: "2%" }}>
              <div>
                <div style={{ fontSize: 8, color: "rgba(255,255,255,0.3)", letterSpacing: "0.2em", fontFamily: "'Montserrat',sans-serif", marginBottom: 3 }}>CARD HOLDER</div>
                <div style={{ fontSize: "clamp(10px,2.2vw,13px)", color: "rgba(255,255,255,0.8)", fontWeight: 500, fontFamily: "'Montserrat',sans-serif", letterSpacing: "0.08em" }}>
                  {holderName.toUpperCase() || "COSMOOLA USER"}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 8, color: "rgba(255,255,255,0.3)", letterSpacing: "0.2em", fontFamily: "'Montserrat',sans-serif", marginBottom: 3 }}>EXPIRES</div>
                <div style={{ fontSize: "clamp(10px,2.2vw,13px)", color: "rgba(255,255,255,0.8)", fontFamily: "'Courier New',monospace", fontWeight: 500 }}>{expDisplay}</div>
              </div>
              {/* Limit badge */}
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 8, color: "rgba(255,255,255,0.3)", letterSpacing: "0.15em", fontFamily: "'Montserrat',sans-serif", marginBottom: 3 }}>LIMIT</div>
                <div style={{ fontSize: 9, color: D.gold, fontFamily: "'Montserrat',sans-serif", fontWeight: 600 }}>
                  €{(card.spendingLimitEUR / 1_000_000).toFixed(0)}M
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── BACK ── */}
        <div style={{
          position: "absolute", inset: 0, backfaceVisibility: "hidden",
          transform: "rotateY(180deg)", borderRadius: 16,
          background: "linear-gradient(135deg, #0d0d0d, #181818)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.7), 0 0 0 1px #222",
          overflow: "hidden",
        }}>
          {/* Magnetic stripe */}
          <div style={{ position: "absolute", top: "18%", left: 0, right: 0, height: "16%", background: "#1a1a1a", borderTop: "1px solid #222", borderBottom: "1px solid #222" }} />

          {/* CVV strip */}
          <div style={{ position: "absolute", top: "42%", right: "8%", left: "8%" }}>
            <div style={{ background: "#f5f5f0", padding: "8px 12px", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <div style={{ fontFamily: "'Courier New',monospace", fontSize: 16, letterSpacing: "0.3em", color: "#1a1a1a", fontWeight: 700 }}>
                {revealed && cvv ? cvv : "•••"}
              </div>
            </div>
            <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", textAlign: "right", marginTop: 4, fontFamily: "'Montserrat',sans-serif", letterSpacing: "0.15em" }}>CVV / CVC</div>
          </div>

          {/* Back brand */}
          <div style={{ position: "absolute", bottom: "12%", left: "8%", right: "8%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", fontFamily: "'Montserrat',sans-serif", letterSpacing: "0.15em", lineHeight: 1.8 }}>
              This card is issued by Cosmoola Technologies OÜ.<br/>
              Use is subject to the Cosmoola Cardholder Agreement.
            </div>
            {isVisa
              ? <div style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 700, fontSize: 18, color: D.gold, opacity: 0.6 }}>VISA</div>
              : <div style={{ display: "flex" }}>
                  <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#eb001b", opacity: 0.6 }} />
                  <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#f79e1b", marginLeft: -11, opacity: 0.55 }} />
                </div>
            }
          </div>
        </div>
      </motion.div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   ISSUE CARD MODAL
════════════════════════════════════════════════════════════ */
const IssueCardModal: React.FC<{
  open: boolean;
  userId: string;
  userAddress: any;
  firstName: string;
  lastName: string;
  email: string;
  onSuccess: (card: IssuedCard) => void;
  onClose: () => void;
}> = ({ open, userId, userAddress, firstName, lastName, email, onSuccess, onClose }) => {
  const [cardBrand, setCardBrand] = useState<"visa" | "mastercard">("visa");
  const [cardType, setCardType]   = useState<"virtual" | "physical">("virtual");
  const [step, setStep]           = useState<"select" | "loading" | "error">("select");
  const [errorMsg, setErrorMsg]   = useState("");

  const brandCfg = BRAND_CFG[cardBrand];

  const handleIssue = async () => {
    setStep("loading");
    try {
      // Step 1: ensure cardholder exists
      await axios.post(`${API}/createCardHolder`, {
        userId, firstName, lastName, email,
        address: {
          line1:      userAddress?.streetAddress || "1 Cosmoola Street",
          city:       userAddress?.city          || "Tallinn",
          state:      userAddress?.state         || undefined,
          postalCode: userAddress?.zipCode       || "10111",
          country:    userAddress?.countryCode   || "EE",
        },
      });

      // Step 2: issue the card
      const { data } = await axios.post(`${API}/issueCard`, { userId, type: cardType, brand: cardBrand });
      onSuccess({
        id:               data.cardId,
        stripeCardId:     data.cardId,
        type:             data.type,
        status:           data.status,
        brand:            data.brand          ?? cardBrand,
        requestedBrand:   data.requestedBrand ?? cardBrand,
        last4:            data.last4,
        expMonth:         data.expMonth,
        expYear:          data.expYear,
        currency:         data.currency,
        spendingLimitEUR: data.spendingLimitEUR,
        spendingInterval: "all_time",
        isDefault:        data.isDefault,
        isIssued:         true,
        createdAt:        new Date().toISOString(),
      });
      onClose();
    } catch (err: any) {
      setErrorMsg(err.response?.data?.error || err.message || "Failed to issue card");
      setStep("error");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", backdropFilter: "blur(10px)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={onClose}>
          <motion.div initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24 }} transition={{ duration: 0.3 }}
            onClick={e => e.stopPropagation()}
            style={{ background: D.bg, border: `1px solid ${D.border2}`, maxWidth: 440, width: "100%", padding: "36px 32px" }}>

            {step === "select" && (<>
              {/* Header */}
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:26 }}>
                <div>
                  <h2 style={{ fontSize:26, fontWeight:300, color:D.white, fontFamily:"'Cormorant Garamond',serif", letterSpacing:"0.04em", margin:0, lineHeight:1 }}>
                    Issue New Card
                  </h2>
                  <p style={{ fontSize:10, color:D.muted, marginTop:7, letterSpacing:"0.07em" }}>
                    €30,000,000 limit · Stripe Issuing · Worldwide
                  </p>
                </div>
                <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:D.muted, padding:"2px 4px" }}>
                  <X size={17}/>
                </button>
              </div>

              {/* ── BRAND SELECTION ── */}
              <p style={{ fontSize:9, color:D.muted, letterSpacing:"0.24em", marginBottom:14 }}>SELECT NETWORK</p>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:24 }}>
                {(["visa","mastercard"] as const).map(b => {
                  const bcfg = BRAND_CFG[b];
                  const isSelected = cardBrand === b;
                  return (
                    <motion.button key={b} whileTap={{ scale:0.96 } as any} onClick={() => setCardBrand(b)}
                      style={{ padding:0, background:"none", border:"none", cursor:"pointer", textAlign:"center" }}>
                      {/* Mini card preview */}
                      <div style={{
                        borderRadius:10, padding:"14px 14px 12px",
                        background: b === "visa" ? "linear-gradient(145deg,#0a0a0a,#181818,#0c0c0c)" : "linear-gradient(145deg,#120818,#1d1128,#0e0614)",
                        border: isSelected ? `1.5px solid ${bcfg.accent}` : `1px solid #1e1e1e`,
                        marginBottom:10, position:"relative", overflow:"hidden",
                        boxShadow: isSelected ? `0 0 0 1px ${bcfg.accent}18,0 8px 24px rgba(0,0,0,0.65)` : "0 4px 14px rgba(0,0,0,0.45)",
                        transition:"all 0.24s",
                      }}>
                        {/* Shimmer */}
                        <div style={{ position:"absolute", inset:0, background: b==="visa" ? "radial-gradient(ellipse at 72% 28%,rgba(201,169,110,0.15),transparent 65%)" : "radial-gradient(ellipse at 72% 28%,rgba(235,0,27,0.15) 0%,rgba(247,158,27,0.08) 40%,transparent 65%)" }}/>
                        {/* Texture */}
                        <svg style={{ position:"absolute", inset:0, width:"100%", height:"100%", opacity:0.038 }} preserveAspectRatio="none">
                          <defs><pattern id={`mt-${b}`} x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse" patternTransform="rotate(37)"><line x1="0" y1="0" x2="0" y2="14" stroke="white" strokeWidth="0.7"/></pattern></defs>
                          <rect width="100%" height="100%" fill={`url(#mt-${b})`}/>
                        </svg>
                        <div style={{ position:"relative" }}>
                          {/* Top row */}
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                            <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                              <img src={logo} alt="" style={{ width:16, height:16, borderRadius:3, objectFit:"cover" }}/>
                              <span style={{ fontSize:6, fontWeight:700, color:bcfg.accent, letterSpacing:"0.28em", fontFamily:"'Montserrat',sans-serif" }}>COSMOOLA</span>
                            </div>
                            {bcfg.frontMark()}
                          </div>
                          {/* Chip */}
                          <div style={{ width:24, height:16, borderRadius:3, background:"linear-gradient(135deg,#c0922a,#eac96a,#a07030)", marginBottom:12 }}/>
                          {/* PAN */}
                          <div style={{ fontSize:7, color:"rgba(255,255,255,0.4)", fontFamily:"'Courier New',monospace", letterSpacing:"0.15em", marginBottom:2 }}>•••• •••• •••• ••••</div>
                          <div style={{ display:"flex", justifyContent:"flex-end" }}>
                            <span style={{ fontSize:7, color:bcfg.accent, fontFamily:"'Montserrat',sans-serif", fontWeight:700 }}>€30M LIMIT</span>
                          </div>
                        </div>
                        {/* Selected tick */}
                        {isSelected && (
                          <div style={{ position:"absolute", top:7, right:7, width:17, height:17, borderRadius:"50%", background:bcfg.accent, display:"flex", alignItems:"center", justifyContent:"center" }}>
                            <Check size={9} color="#000" strokeWidth={3}/>
                          </div>
                        )}
                      </div>
                      {/* Label */}
                      <div style={{ fontSize:11, fontWeight:600, letterSpacing:"0.16em", fontFamily:"'Montserrat',sans-serif", color:isSelected?bcfg.accent:D.muted, transition:"color 0.2s", marginBottom:2 }}>
                        {bcfg.label.toUpperCase()}
                      </div>
                      <div style={{ fontSize:9, color:D.muted }}>{bcfg.description}</div>
                    </motion.button>
                  );
                })}
              </div>

              {/* ── TYPE SELECTION ── */}
              <p style={{ fontSize:9, color:D.muted, letterSpacing:"0.24em", marginBottom:12 }}>CARD FORMAT</p>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:24 }}>
                {(["virtual","physical"] as const).map(t => (
                  <motion.button key={t} whileTap={{ scale:0.97 } as any} onClick={() => setCardType(t)}
                    style={{ padding:"16px 12px", background:cardType===t?D.surface2:"transparent", border:cardType===t?`1px solid ${brandCfg.accent}`:`1px solid ${D.border}`, cursor:"pointer", textAlign:"center", transition:"all 0.2s" }}>
                    <div style={{ marginBottom:8, color:cardType===t?brandCfg.accent:D.muted, display:"flex", justifyContent:"center" }}>
                      {t==="virtual"?<Smartphone size={20}/>:<CreditCard size={20}/>}
                    </div>
                    <div style={{ fontSize:11, fontWeight:600, letterSpacing:"0.16em", fontFamily:"'Montserrat',sans-serif", color:cardType===t?brandCfg.accent:D.offwhite }}>
                      {t.toUpperCase()}
                    </div>
                    <div style={{ fontSize:9, color:D.muted, marginTop:4, lineHeight:1.6 }}>
                      {t==="virtual"?"Instant · Apple / Google Pay":"Shipped in 3–5 days"}
                    </div>
                  </motion.button>
                ))}
              </div>

              {/* ── LIMIT INFO ── */}
              <div style={{ background:D.surface, border:`1px solid ${D.border}`, padding:"14px 16px", marginBottom:24, display:"flex", alignItems:"center", gap:12 }}>
                <Zap size={15} color={brandCfg.accent} style={{ flexShrink:0 }}/>
                <div>
                  <div style={{ fontSize:11, color:D.offwhite, fontWeight:600 }}>Spending limit: €30,000,000</div>
                  <div style={{ fontSize:9, color:D.muted, marginTop:3 }}>All-time · {brandCfg.network} · Accepted globally</div>
                </div>
              </div>

              {/* ── ISSUE BUTTON ── */}
              <motion.button whileTap={{ scale:0.98 } as any} onClick={handleIssue}
                style={{ width:"100%", padding:"15px 0", background:brandCfg.accent, border:"none", color:"#000", cursor:"pointer", fontSize:11, fontWeight:700, letterSpacing:"0.22em", fontFamily:"'Montserrat',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                <Plus size={13}/>
                ISSUE {cardBrand.toUpperCase()} {cardType.toUpperCase()}
              </motion.button>
            </>)}

            {step === "loading" && (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <Loader2 size={32} color={D.gold} className="cards-spin" style={{ marginBottom: 16 }} />
                <div style={{ fontSize: 20, fontWeight: 300, color: D.white, fontFamily: "'Cormorant Garamond',serif", letterSpacing: "0.05em", marginBottom:8 }}>Issuing your card</div>
                <div style={{ fontSize: 11, color: D.muted }}>Setting up with Stripe Issuing · {brandCfg.label}</div>
              </div>
            )}

            {step === "error" && (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <AlertCircle size={32} color="#B22222" style={{ marginBottom: 16 }} />
                <div style={{ fontSize: 16, color: D.white, fontFamily: "'Cormorant Garamond',serif", marginBottom: 8 }}>Could not issue card</div>
                <div style={{ fontSize: 11, color: D.muted, marginBottom: 24, lineHeight: 1.7 }}>{errorMsg}</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={onClose} style={{ flex: 1, padding: "12px 0", background: "transparent", border: `1px solid ${D.border}`, color: D.muted, cursor: "pointer", fontSize: 11, fontFamily: "'Montserrat',sans-serif" }}>CLOSE</button>
                  <button onClick={() => setStep("select")} style={{ flex: 1, padding: "12px 0", background: D.surface2, border: `1px solid ${D.border2}`, color: D.offwhite, cursor: "pointer", fontSize: 11, fontFamily: "'Montserrat',sans-serif" }}>TRY AGAIN</button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN PAGE
════════════════════════════════════════════════════════════ */
const Cards: React.FC = () => {
  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();
  const { userId: paramUserId } = useParams<{ userId: string }>();
  const { user }  = useSelector((s: RootState) => s.auth);
  const userId    = paramUserId ?? user?.userId ?? "";
  const holderName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");

  const [cards, setCards]           = useState<IssuedCard[]>([]);
  const [activeIdx, setActiveIdx]   = useState(0);
  const [loading, setLoading]       = useState(true);
  const [loaded, setLoaded]         = useState(false);
  const [flipped, setFlipped]       = useState(false);
  const [revealed, setRevealed]     = useState(false);
  const [pan, setPan]               = useState("");
  const [cvv, setCvv]               = useState("");
  const [revealLoading, setRevealLoading] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [toast, setToast]           = useState<{ success: boolean; msg: string } | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [copied, setCopied]         = useState(false);
  const toastRef = useRef<NodeJS.Timeout | null>(null);

  /* ── Load issued cards ── */
  useEffect(() => {
    dispatch(fetchUserData());
    if (userId) loadCards();
    setTimeout(() => setLoaded(true), 80);
  }, [userId]);

  const loadCards = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/listIssuedCards`, { userId });
      const sorted = [...(data.cards ?? [])].sort((a: IssuedCard, b: IssuedCard) => {
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;
        return 0;
      });
      setCards(sorted);
      setActiveIdx(0);
    } catch (err: any) {
      showToast(false, err.response?.data?.error || "Failed to load cards");
    } finally { setLoading(false); }
  };

  const showToast = (success: boolean, msg: string) => {
    if (toastRef.current) clearTimeout(toastRef.current);
    setToast({ success, msg });
    toastRef.current = setTimeout(() => setToast(null), 4000);
  };

  const activeCard = cards[activeIdx] ?? null;

  /* ── Reveal PAN ── */
  const handleReveal = async () => {
    if (!activeCard) return;
    if (revealed) { setRevealed(false); setPan(""); setCvv(""); setFlipped(false); return; }
    setRevealLoading(true);
    try {
      const { data } = await axios.post(`${API}/getIssuedCardDetails`, { userId, cardId: activeCard.id });
      // In production: use data.ephemeralKeySecret + Stripe.js to fetch PAN
      // For display purposes we show what's available:
      setPan(data.last4 ? `**** **** **** ${data.last4}` : "");
      setCvv("•••"); // PAN reveal requires Stripe.js ephemeral key on client
      setRevealed(true);
    } catch (err: any) {
      showToast(false, err.response?.data?.error || "Could not retrieve card details");
    } finally { setRevealLoading(false); }
  };

  /* ── Copy card number ── */
  const handleCopy = () => {
    if (!activeCard) return;
    navigator.clipboard.writeText(activeCard.last4);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  /* ── Freeze / unfreeze ── */
  const handleFreeze = async () => {
    if (!activeCard) return;
    const newStatus = activeCard.status === "active" ? "inactive" : "active";
    setActionLoading("freeze");
    try {
      await axios.post(`${API}/updateIssuedCardStatus`, { userId, cardId: activeCard.id, status: newStatus });
      setCards(prev => prev.map((c, i) => i === activeIdx ? { ...c, status: newStatus } : c));
      showToast(true, newStatus === "inactive" ? "Card frozen" : "Card activated");
    } catch (err: any) {
      showToast(false, err.response?.data?.error || "Could not update card");
    } finally { setActionLoading(null); }
  };

  /* ── Set default ── */
  const handleSetDefault = async () => {
    if (!activeCard) return;
    setActionLoading("default");
    try {
      await axios.post(`${API}/setDefaultIssuedCard`, { userId, cardId: activeCard.id });
      setCards(prev => prev.map((c, i) => ({ ...c, isDefault: i === activeIdx })));
      showToast(true, "Default card updated");
    } catch (err: any) {
      showToast(false, err.response?.data?.error || "Could not update default");
    } finally { setActionLoading(null); }
  };

  /* ── Cancel card ── */
  const handleCancel = async () => {
    if (!activeCard || activeCard.status === "canceled") return;
    if (!window.confirm("Cancel this card permanently? This cannot be undone.")) return;
    setActionLoading("cancel");
    try {
      await axios.post(`${API}/updateIssuedCardStatus`, { userId, cardId: activeCard.id, status: "canceled" });
      setCards(prev => prev.map((c, i) => i === activeIdx ? { ...c, status: "canceled" } : c));
      showToast(true, "Card canceled");
    } catch (err: any) {
      showToast(false, err.response?.data?.error || "Could not cancel card");
    } finally { setActionLoading(null); }
  };

  return (
    <div style={{ minHeight: "100vh", background: D.bg, color: D.white, fontFamily: "'Montserrat','Cormorant Garamond',system-ui,sans-serif" }}>
      <style>{CARDS_CSS}</style>

      {/* ── HEADER ── */}
      <header style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(0,0,0,0.96)", backdropFilter: "blur(16px)", borderBottom: `1px solid ${D.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px", height: 58 }}>
        <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: D.offwhite, display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontFamily: "'Montserrat',sans-serif", letterSpacing: "0.05em" }}>
          <ArrowLeft size={16} strokeWidth={1.5} />
          <span className="cards-back-label">BACK</span>
        </button>
        <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)" }}>
          <span style={{ color: D.white, fontWeight: 300, letterSpacing: "0.35em", fontFamily: "'Cormorant Garamond',serif", fontSize: 15 }}>COSMOOLA</span>
        </div>
        <img src={logo} alt="C" style={{ width: 28, height: 28, objectFit: "cover" }} />
      </header>

      <div className="cards-container">

        {/* ── TITLE ── */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 14 }} transition={{ duration: 0.4 }} style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 32, fontWeight: 300, color: D.white, fontFamily: "'Cormorant Garamond',serif", letterSpacing: "0.06em", margin: 0 }}>My Cards</h1>
          <p style={{ fontSize: 11, color: D.muted, marginTop: 8, letterSpacing: "0.06em" }}>
            {activeCard
              ? `${getCfg(activeCard.brand).label} · Powered by Stripe Issuing`
              : "Visa & Mastercard · Powered by Stripe Issuing"
            }
          </p>
        </motion.div>

        {/* ── LOADING STATE ── */}
        {loading && (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <Loader2 size={28} color={D.gold} className="cards-spin" style={{ marginBottom: 16 }} />
            <p style={{ fontSize: 13, color: D.muted }}>Loading your cards...</p>
          </div>
        )}

        {/* ── EMPTY STATE ── */}
        {!loading && cards.length === 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            style={{ textAlign: "center", padding: "60px 0" }}>
            {/* Dual brand ghost cards */}
            <div style={{ display:"flex", justifyContent:"center", gap:14, marginBottom:32, position:"relative" }}>
              {(["visa","mastercard"] as const).map((b,i) => (
                <div key={b} style={{
                  width:130, height:80, borderRadius:10, border:`1px dashed ${D.border2}`,
                  background: b==="visa" ? "linear-gradient(145deg,#0a0a0a,#181818)" : "linear-gradient(145deg,#120818,#1d1128)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  opacity:0.48+i*0.18,
                  transform:`rotate(${i===0?-4:4}deg)`,
                  boxShadow:"0 8px 26px rgba(0,0,0,0.55)",
                }}>
                  <div style={{ opacity:0.38 }}>{BRAND_CFG[b].frontMark()}</div>
                </div>
              ))}
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 300, color: D.offwhite, fontFamily: "'Cormorant Garamond',serif", letterSpacing: "0.04em", marginBottom: 10 }}>No cards yet</h2>
            <p style={{ fontSize: 12, color: D.muted, lineHeight: 1.8, maxWidth: 320, margin: "0 auto 28px" }}>Issue a Visa or Mastercard with a €30,000,000 spending limit. Virtual cards activate instantly.</p>
            <motion.button whileTap={{ scale: 0.97 } as any} onClick={() => setShowIssueModal(true)}
              style={{ background: D.gold, color: D.bg, border: "none", padding: "14px 32px", cursor: "pointer", fontSize: 11, fontWeight: 700, letterSpacing: "0.2em", fontFamily: "'Montserrat',sans-serif", display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Plus size={14} /> ISSUE YOUR FIRST CARD
            </motion.button>
          </motion.div>
        )}

        {/* ── CARDS VIEW ── */}
        {!loading && cards.length > 0 && (
          <>
            {/* ── CARD CAROUSEL ── */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 20 }} transition={{ duration: 0.45, delay: 0.1 }}>

              {/* Card visual */}
              <CardVisual
                card={activeCard}
                holderName={holderName}
                revealed={revealed}
                pan={pan}
                cvv={cvv}
                flipped={flipped}
                onClick={() => setFlipped(f => !f)}
              />

              {/* Flip hint */}
              <p style={{ textAlign: "center", fontSize: 10, color: D.muted, marginTop: 10, letterSpacing: "0.15em" }}>
                TAP TO FLIP
              </p>

              {/* Card dots */}
              {cards.length > 1 && (
                <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16 }}>
                  {cards.map((_, i) => (
                    <button key={i} onClick={() => { setActiveIdx(i); setRevealed(false); setFlipped(false); }}
                      style={{ width: i === activeIdx ? 22 : 6, height: 6, borderRadius: 3, padding: 0, border: "none", cursor: "pointer", background: i === activeIdx ? getCfg(activeCard.brand).accent : D.border2, transition: "all 0.25s" }} />
                  ))}
                </div>
              )}
            </motion.div>

            {/* ── CARD INFO STRIP ── */}
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 14 }} transition={{ duration: 0.4, delay: 0.2 }}
              style={{ background: D.surface, border: `1px solid ${D.border}`, padding: "20px 22px", marginTop: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 9, color: D.muted, letterSpacing: "0.22em", marginBottom: 6, display:"flex", alignItems:"center", gap:8 }}>
                    <span>{activeCard.brand.toUpperCase()}</span>
                    <span style={{ color:D.border3 }}>·</span>
                    <span>{activeCard.type.toUpperCase()}</span>
                    {activeCard.requestedBrand && activeCard.requestedBrand !== activeCard.brand && (
                      <span style={{ fontSize:8, color:"#b8860b" }}>(requested {activeCard.requestedBrand})</span>
                    )}
                  </div>
                  <div style={{ fontSize: 18, color: D.offwhite, fontFamily: "'Courier New',monospace", letterSpacing: "0.15em" }}>
                    •••• •••• •••• {activeCard.last4}
                  </div>
                </div>
                {/* Status pill */}
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 12px",
                  background: activeCard.status === "active" ? "rgba(0,180,80,0.1)" : activeCard.status === "inactive" ? "rgba(50,100,255,0.1)" : "rgba(180,30,30,0.1)",
                  border: `1px solid ${activeCard.status === "active" ? "rgba(0,180,80,0.3)" : activeCard.status === "inactive" ? "rgba(80,130,255,0.3)" : "rgba(200,50,50,0.3)"}`,
                }}>
                  <div style={{ width: 5, height: 5, borderRadius: "50%", background: activeCard.status === "active" ? "#00cc55" : activeCard.status === "inactive" ? "#5090ff" : "#cc3030" }} />
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.2em", fontFamily: "'Montserrat',sans-serif", color: activeCard.status === "active" ? "#00cc55" : activeCard.status === "inactive" ? "#5090ff" : "#cc3030" }}>
                    {activeCard.status === "inactive" ? "FROZEN" : activeCard.status.toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Metadata row */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 0, borderTop: `1px solid ${D.border}`, paddingTop: 14 }}>
                {[
                  { label: "EXPIRES",  value: `${String(activeCard.expMonth).padStart(2,"0")}/${String(activeCard.expYear).slice(-2)}` },
                  { label: "LIMIT",    value: `€${(activeCard.spendingLimitEUR/1_000_000).toFixed(0)}M` },
                  { label: "NETWORK", value: getCfg(activeCard.brand).label.toUpperCase() },
                ].map((s, i) => (
                  <div key={s.label} style={{ paddingLeft: i > 0 ? 16 : 0, borderLeft: i > 0 ? `1px solid ${D.border}` : "none" }}>
                    <div style={{ fontSize: 9, color: D.muted, letterSpacing: "0.2em", marginBottom: 5 }}>{s.label}</div>
                    <div style={{ fontSize: 14, fontWeight: 300, color: D.offwhite, fontFamily: "'Cormorant Garamond',serif" }}>{s.value}</div>
                  </div>
                ))}
              </div>
              {activeCard.isDefault && (
                <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 4, height: 4, borderRadius: "50%", background: getCfg(activeCard.brand).accent }} />
                  <span style={{ fontSize: 9, color: getCfg(activeCard.brand).accent, letterSpacing: "0.2em", fontFamily: "'Montserrat',sans-serif" }}>DEFAULT CARD</span>
                </div>
              )}
            </motion.div>

            {/* ── ACTION BUTTONS ── */}
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 14 }} transition={{ duration: 0.4, delay: 0.25 }}
              style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginTop: 16 }}>
              {[
                {
                  key:  "reveal",
                  icon: revealed
                    ? (revealLoading ? <Loader2 size={18} className="cards-spin" /> : <EyeOff size={18} />)
                    : (revealLoading ? <Loader2 size={18} className="cards-spin" /> : <Eye size={18} />),
                  label: revealed ? "HIDE" : "REVEAL",
                  action: handleReveal,
                  disabled: revealLoading || activeCard.status === "canceled",
                },
                {
                  key:  "copy",
                  icon: copied ? <Check size={18} /> : <Copy size={18} />,
                  label: copied ? "COPIED" : "COPY",
                  action: handleCopy,
                  disabled: activeCard.status === "canceled",
                },
                {
                  key:  "freeze",
                  icon: actionLoading === "freeze" ? <Loader2 size={18} className="cards-spin" /> : activeCard.status === "inactive" ? <Zap size={18} /> : <Snowflake size={18} />,
                  label: activeCard.status === "inactive" ? "UNFREEZE" : "FREEZE",
                  action: handleFreeze,
                  disabled: actionLoading === "freeze" || activeCard.status === "canceled",
                  accent: activeCard.status === "inactive",
                },
                {
                  key:  "default",
                  icon: actionLoading === "default" ? <Loader2 size={18} className="cards-spin" /> : <CheckCircle size={18} />,
                  label: "DEFAULT",
                  action: handleSetDefault,
                  disabled: activeCard.isDefault || actionLoading === "default" || activeCard.status === "canceled",
                  active: activeCard.isDefault,
                },
              ].map(btn => (
                <motion.button key={btn.key} whileTap={{ scale: 0.94 } as any} onClick={btn.action}
                  disabled={btn.disabled}
                  style={{
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 7,
                    padding: "16px 8px", background: btn.active ? D.surface2 : "transparent",
                    border: `1px solid ${btn.accent ? "rgba(80,130,255,0.4)" : btn.active ? D.gold + "55" : D.border}`,
                    cursor: btn.disabled ? "not-allowed" : "pointer",
                    color: btn.disabled ? D.subtle : btn.accent ? "#5090ff" : btn.active ? D.gold : D.offwhite,
                    opacity: btn.disabled && !btn.active ? 0.4 : 1,
                    transition: "all 0.2s",
                  }}>
                  {btn.icon}
                  <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", fontFamily: "'Montserrat',sans-serif" }}>{btn.label}</span>
                </motion.button>
              ))}
            </motion.div>

            {/* ── ALL CARDS LIST ── */}
            {cards.length > 1 && (
              <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 14 }} transition={{ duration: 0.4, delay: 0.3 }} style={{ marginTop: 28 }}>
                <p style={{ fontSize: 10, color: D.muted, letterSpacing: "0.2em", marginBottom: 14 }}>ALL CARDS</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {cards.map((card, i) => (
                    <motion.div key={card.id} whileTap={{ scale: 0.99 } as any} onClick={() => { setActiveIdx(i); setRevealed(false); setFlipped(false); }}
                      style={{
                        display: "flex", alignItems: "center", gap: 14, padding: "14px 18px",
                        background: i === activeIdx ? D.surface : "transparent",
                        border: `1px solid ${i === activeIdx ? D.border2 : D.border}`,
                        cursor: "pointer", transition: "all 0.2s",
                      }}>
                      {/* Mini card chip */}
                      <div style={{
                        width: 44, height: 30, borderRadius: 4, flexShrink: 0,
                        background: card.brand.toLowerCase() === "mastercard"
                          ? "linear-gradient(135deg, #1a1a2e, #0f0f1a)"
                          : "linear-gradient(135deg, #0a0a0a, #1a1a1a)",
                        border: `1px solid ${D.border2}`, display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {card.brand.toLowerCase() === "mastercard" ? (
                          <div style={{ display: "flex" }}>
                            <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#eb001b", opacity: 0.8 }} />
                            <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#f79e1b", marginLeft: -6, opacity: 0.75 }} />
                          </div>
                        ) : (
                          <span style={{ fontSize: 8, fontWeight: 700, color: D.gold, fontFamily: "'Cormorant Garamond',serif" }}>VISA</span>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, color: D.offwhite, fontFamily: "'Courier New',monospace", letterSpacing: "0.1em" }}>
                          •••• {card.last4}
                        </div>
                        <div style={{ fontSize: 10, color: D.muted, marginTop: 2, letterSpacing: "0.05em" }}>
                          {card.type} · Expires {String(card.expMonth).padStart(2,"0")}/{String(card.expYear).slice(-2)}
                          {card.isDefault && <span style={{ marginLeft: 8, color: D.gold, fontWeight: 600 }}>DEFAULT</span>}
                        </div>
                      </div>
                      {/* Status dot */}
                      <div style={{
                        width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                        background: card.status === "active" ? "#00cc55" : card.status === "inactive" ? "#5090ff" : "#cc3030",
                        boxShadow: card.status === "active" ? "0 0 6px rgba(0,200,80,0.4)" : "none",
                      }} />
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* ── CANCEL + SECURITY ── */}
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 14 }} transition={{ duration: 0.4, delay: 0.35 }}
              style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 12 }}>

              {activeCard.status !== "canceled" && (
                <button onClick={handleCancel} disabled={actionLoading === "cancel"}
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 16px", background: "transparent", border: `1px solid rgba(180,30,30,0.3)`, cursor: "pointer", color: "rgba(200,80,80,0.8)", fontSize: 11, fontFamily: "'Montserrat',sans-serif", letterSpacing: "0.1em" }}>
                  {actionLoading === "cancel" ? <Loader2 size={13} className="cards-spin" /> : <Trash2 size={13} />}
                  CANCEL THIS CARD
                </button>
              )}

              {/* Security note */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "14px 16px", background: D.surface, border: `1px solid ${D.border}` }}>
                <Shield size={14} color={D.gold} style={{ flexShrink: 0, marginTop: 1 }} />
                <span style={{ fontSize: 10, color: D.muted, lineHeight: 1.8 }}>
                  Cards are issued via Stripe Issuing and protected by Visa's Zero Liability Policy. PAN details are encrypted end-to-end and never stored by Cosmoola.
                </span>
              </div>
            </motion.div>
          </>
        )}

        {/* ── ISSUE NEW CARD BUTTON ── */}
        {!loading && (
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 14 }} transition={{ duration: 0.4, delay: 0.4 }}
            style={{ marginTop: 24 }}>
            <motion.button whileTap={{ scale: 0.97 } as any} onClick={() => setShowIssueModal(true)}
              style={{
                width: "100%", padding: "14px 0",
                background: "transparent", border: `1px dashed ${D.border2}`,
                cursor: "pointer", color: D.muted, fontSize: 11, fontWeight: 500,
                letterSpacing: "0.15em", fontFamily: "'Montserrat',sans-serif",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                transition: "border-color 0.2s, color 0.2s",
              }}>
              <Plus size={13} /> ISSUE NEW CARD
            </motion.button>
          </motion.div>
        )}
      </div>

      {/* ── MODALS ── */}
      <IssueCardModal
        open={showIssueModal}
        userId={userId}
        userAddress={user?.address}
        firstName={user?.firstName ?? ""}
        lastName={user?.lastName ?? ""}
        email={user?.email ?? user?.emailAddress ?? ""}
        onSuccess={card => { setCards(prev => [card, ...prev]); setActiveIdx(0); showToast(true, `${getCfg(card.brand).label} ${card.type} card issued successfully`); }}
        onClose={() => setShowIssueModal(false)}
      />

      {/* ── TOAST ── */}
      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }} transition={{ duration: 0.3 }}
            style={{
              position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
              background: toast.success ? D.surface : "#B22222", color: D.white,
              padding: "14px 24px", display: "flex", alignItems: "center", gap: 12,
              border: toast.success ? `1px solid ${D.gold}` : "1px solid #D44",
              zIndex: 9999, maxWidth: 480, width: "90%",
            }}>
            {toast.success ? <CheckCircle size={18} color={D.gold} /> : <AlertCircle size={18} />}
            <span style={{ fontSize: 12, flex: 1 }}>{toast.msg}</span>
            <button onClick={() => setToast(null)} style={{ background: "none", border: "none", cursor: "pointer", color: D.muted, padding: 0 }}><X size={15} /></button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── FOOTER ── */}
      <footer style={{ borderTop: `1px solid ${D.border}`, padding: "24px 20px", marginTop: 40 }}>
        <div style={{ maxWidth: 680, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div style={{ fontSize: 11, color: D.muted }}>© 2025 – {new Date().getFullYear()} Cosmoola Technologies</div>
          <div style={{ display: "flex", gap: 20 }}>
            {["privacy","terms","support"].map(p => (
              <span key={p} onClick={() => navigate(`/${p}`)} style={{ fontSize: 11, color: D.muted, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </span>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Cards;

/* ─── CSS ─── */
const CARDS_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  body{background:#000}button{font-family:inherit}
  ::-webkit-scrollbar{width:0;height:0}
  @keyframes cards-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
  .cards-spin{animation:cards-spin 1s linear infinite}
  .cards-container{
    max-width:520px;
    margin:0 auto;
    padding:32px 20px 0;
  }
  @keyframes shimmer {
    0%{background-position:-200% center}
    100%{background-position:200% center}
  }
  .cards-back-label{display:inline}
  @media(max-width:480px){
    .cards-container{padding:20px 16px 0}
    .cards-back-label{display:none}
  }
`;